import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';

import { isMobileSafari, isMobileUA, isNativeAppUA } from 'dpl/shared/utils';
import { GDLinkV1 } from 'dpl/common/core';
import { BUYER_APP_STORE_URL } from 'dpl/shared/constants/urls';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { ANALYTICS_EVENTS } from 'dpl/constants/analytics';
import { postReactNativeCloseWebViewMessage } from 'dpl/native_app/utils';

const IS_MOBILE_UA = isMobileUA();
const IS_MOBILE_SAFARI = isMobileSafari();
const IS_NATIVE_UA = isNativeAppUA();

export default function BuyerProfileSuccess({ messagesUrl, breederFirstName }) {
  if (IS_NATIVE_UA) {
    return <Native breederFirstName={breederFirstName} />;
  }

  if (IS_MOBILE_UA) {
    return (
      <Mobile messagesUrl={messagesUrl} breederFirstName={breederFirstName} />
    );
  }

  return (
    <Desktop messagesUrl={messagesUrl} breederFirstName={breederFirstName} />
  );
}

BuyerProfileSuccess.propTypes = {
  messagesUrl: PropTypes.string.isRequired,
  breederFirstName: PropTypes.string.isRequired
};

function trackMessengerButtonClicked() {
  sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
    button_cta: 'Go to messenger',
    view: 'contact_submission_success'
  });
}

function trackAppStoreButtonClicked() {
  sendRudderstackEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
    button_cta: 'App store images',
    view: 'contact_submission_success'
  });
}

function Desktop({ messagesUrl, breederFirstName }) {
  return (
    <div className="min-h-100 flex justify-center items-center BuyerProfileSuccess">
      <div className="SectionLayout pv4 ph4 tc">
        <h4 className="mb4">Your request was sent!</h4>
        <div className="BuyerProfileSuccess__content font-16 mb10">
          <p>{breederFirstName} will get back to you soon.</p>
          <p>
            Make messaging easier by downloading the Good Dog app, and never
            miss an update.
          </p>
        </div>
        <div className="mb8">
          <p className="font-16 fw-medium mb6">Scan the QR Code</p>
          <QRCodeSVG value={BUYER_APP_STORE_URL} size={204} />
        </div>
        <div className="flex justify-center">
          <a
            onClick={trackMessengerButtonClicked}
            className="button bg-white b--light-gray max-content"
            href={messagesUrl}
          >
            Go to messenger
          </a>
        </div>
      </div>
    </div>
  );
}

Desktop.propTypes = {
  messagesUrl: PropTypes.string.isRequired,
  breederFirstName: PropTypes.string.isRequired
};

function Mobile({ messagesUrl, breederFirstName }) {
  const { appleStoreImageUrl, androidStoreImageUrl } = useSelector(
    ({ server }) => server
  );

  return (
    <div className="min-h-100 flex justify-center items-center BuyerProfileSuccess">
      <div className="SectionLayout pv4 ph4 tc">
        <h6 className="mb4">Your request was sent!</h6>
        <div className="BuyerProfileSuccess__content font-16 mb10">
          <p>{breederFirstName} will get back to you soon.</p>
          <p>
            Get the Good Dog app for the premium experience in search and
            messaging, and never miss an update.
          </p>
        </div>
        <div className="mb8">
          <p className="font-16 fw-medium mb6">Download the app</p>
          <GDLinkV1
            onClick={trackAppStoreButtonClicked}
            className="mt3 mb14 flex justify-center items-center BuyerProfileSuccess__appStoreBadgeList"
            href={BUYER_APP_STORE_URL}
          >
            {IS_MOBILE_SAFARI ? (
              <img
                className="ph1 BuyerProfileSuccess__appStoreBadge mb4"
                src={appleStoreImageUrl}
                alt="apple app store badge"
              />
            ) : (
              <img
                className="ph1 BuyerProfileSuccess__appStoreBadge"
                src={androidStoreImageUrl}
                alt="android app store badge"
              />
            )}
          </GDLinkV1>
        </div>
        <div className="flex justify-center">
          <a
            className="button bg-white b--light-gray max-content"
            onClick={trackMessengerButtonClicked}
            href={messagesUrl}
          >
            Go to messenger
          </a>
        </div>
      </div>
    </div>
  );
}

Mobile.propTypes = {
  messagesUrl: PropTypes.string.isRequired,
  breederFirstName: PropTypes.string.isRequired
};

function Native({ breederFirstName }) {
  return (
    <div className="min-h-100 flex justify-center items-center BuyerProfileSuccess">
      <div className="SectionLayout pv4 ph4 tc">
        <h6 className="mb4">Your request was sent!</h6>
        <div className="BuyerProfileSuccess__content font-16 mb10">
          <p>{breederFirstName} will get back to you soon.</p>
          <p>
            You’ll hear back from {breederFirstName} soon. While you wait, jump
            back into your search.
          </p>
        </div>
        <div className="flex justify-center">
          <button
            type="button"
            onClick={postReactNativeCloseWebViewMessage}
            className="button bg-white b--light-gray max-content"
          >
            Continue searching
          </button>
        </div>
      </div>
    </div>
  );
}

Native.propTypes = {
  breederFirstName: PropTypes.string.isRequired
};
